import type { PropsWithChildren } from 'react';
import { Flex, FlexProps, Group } from '@mantine/core';

import Title from '@/components/content/title/Title';

import type { ModuleDef } from '../types/modulesv2';

import classes from './IssueModule.module.css';

interface ModuleWrapperProps<TData> extends FlexProps, Omit<ModuleDef<TData>, 'submodules'> {
  expand?: boolean;
  variant?: string;
}

const ModuleHeading = ({ title, variant }: { title: string; variant: string }) => {
  if (variant === 'actions') {
    return <h2 className={classes.actionsHeading}>{title}</h2>;
  }

  if (variant === 'sidebar') {
    return <h2 className={classes.sidebarHeading}>{title}</h2>;
  }

  return <Title order={2}>{title}</Title>;
};

const ModuleWrapper = <TData extends unknown>({
  title,
  expand,
  children,
  removeBackground,
  removePadding,
  removeMargin,
  icon,
  id,
  noPrint,
  titleSideElement,
  issueId,
  variant = 'default',
  ...flexProps
}: PropsWithChildren<ModuleWrapperProps<TData>>) => {
  const containerClass = `
    ${classes.moduleContainer}
    ${removeMargin ? classes.removeMargin : ''}
    ${noPrint ? 'noPrint' : ''}
    ${expand ? classes.expand : ''}
    ${variant === 'actions' ? classes.actionsContainer : ''}
  `;

  if (variant === 'actions') {
    return (
      <div id={id} className={containerClass}>
        <Flex className={classes.actionsRow} justify="space-between" align="center">
          <Group classNames={{ root: classes.moduleTitle }} gap={5}>
            {title && <ModuleHeading title={title} variant={variant} />}
            {icon}
          </Group>
          <Group className={classes.actionsItems}>
            {titleSideElement?.element && (
              <titleSideElement.element id={`${id}`} loading={false} getValue={() => issueId} />
            )}
            {children}
          </Group>
        </Flex>
      </div>
    );
  }

  return (
    <div id={id} className={containerClass}>
      {title && (
        <Flex justify="space-between">
          <Group classNames={{ root: classes.moduleTitle }} gap={5}>
            <ModuleHeading title={title} variant={variant} />
            {icon}
          </Group>
          <Group>
            {titleSideElement?.element && (
              <titleSideElement.element id={`${id}`} loading={false} getValue={() => issueId} />
            )}
          </Group>
        </Flex>
      )}

      <Flex
        classNames={{
          root: `
        ${classes.module}
        ${expand ? classes.expand : ''}
        ${removePadding ? classes.removePadding : ''}
        ${removeBackground ? classes.removeBackground : ''}
        ${variant === 'sidebar' ? classes.sidebarModule : ''}
      `,
        }}
        gap={15}
        direction="column"
        {...flexProps}
      >
        {children}
      </Flex>
    </div>
  );
};

export default ModuleWrapper;
